import { DOCUMENT } from '@angular/common';
import { Injectable, computed, effect, inject, linkedSignal } from '@angular/core';
import type { StoredNotificationPreferences } from '@common/types';
import { Observable, of, throwError } from 'rxjs';
import { delay } from 'rxjs/operators';

import {
  ACCOUNT_NOTIFICATION_PREFERENCES_SEED,
  DEFAULT_ACCOUNT_NOTIFICATION_PREFERENCE,
  DEFAULT_NOTIFICATION_DELIVERY,
} from '@common/data';
import {
  ANONYMOUS_PREFERENCES_OWNER,
  INSTANT_ALERTS_FEATURE,
  NOTIFICATION_PREFERENCES_KEY_PREFIX,
} from '@common/enums';
import { reconcileNotificationPreferences } from '@common/functions';
import type {
  AccountNotificationPreference,
  AccountNotificationSummary,
  EventNotificationPreference,
  NotificationEventKey,
  PersonalChannel,
  ResolvedEventPreference,
  UserNotificationDelivery,
} from '@common/types';

import { AccountTierService } from './account-tier.service';
import { AuditService } from './audit.service';
import { CurrentUserService } from './current-user.service';
import { NotificationPolicyService } from './notification-policy.service';
import { OrganizationService } from './organization.service';

const SAVE_LATENCY_MS = 260;

/** Covered by each account's review emails and summaries, so they are not repeated per event. */
const ACCOUNT_LEVEL_EVENTS: ReadonlySet<NotificationEventKey> = new Set([
  'review.new',
  'digest.weekly',
]);

/** Low-score email is the per-account "review emails" choice; per event it is in-app only. */
const EMAIL_HANDLED_PER_ACCOUNT: ReadonlySet<NotificationEventKey> = new Set(['review.low-score']);

const PERSONAL_CHANNELS: ReadonlyArray<PersonalChannel> = ['in-app', 'email'];

/**
 * Who is told what, per person. The organisation's notification policy decides what exists and what
 * is mandatory; these rows record each person's choices inside it.
 *
 * A missing row means the person is on the defaults, which is how every user starts and what the
 * admin view shows for someone who has never opened their settings.
 */
@Injectable({ providedIn: 'root' })
export class NotificationPreferencesService {
  private readonly document = inject(DOCUMENT);
  private readonly currentUser = inject(CurrentUserService);
  private readonly policies = inject(NotificationPolicyService);
  private readonly organizations = inject(OrganizationService);
  private readonly tiers = inject(AccountTierService);
  private readonly audit = inject(AuditService);

  private readonly owner = computed<string>(
    () => this.currentUser.sub() ?? ANONYMOUS_PREFERENCES_OWNER,
  );

  /** The signed-in user's rows, reloaded whenever the person signed in changes. */
  private readonly own = linkedSignal<string, StoredNotificationPreferences>({
    source: this.owner,
    computation: (owner) => this.load(owner),
  });

  readonly catalog = this.policies.catalog;

  constructor() {
    effect(() => {
      const owner = this.owner();
      const rows = this.own();
      if (owner === ANONYMOUS_PREFERENCES_OWNER) return;
      try {
        this.document.defaultView?.localStorage.setItem(storageKeyFor(owner), JSON.stringify(rows));
      } catch {}
    });
  }

  /** The person's choices for one account; the defaults when they have not set any. */
  accountPreference(userId: string, accountId: string): AccountNotificationPreference {
    const row = this.accountRows(userId).find((entry) => entry.accountId === accountId);
    return row ?? { userId, accountId, ...DEFAULT_ACCOUNT_NOTIFICATION_PREFERENCE };
  }

  hasCustomAccountPreference(userId: string, accountId: string): boolean {
    return this.accountRows(userId).some((entry) => entry.accountId === accountId);
  }

  delivery(userId: string): UserNotificationDelivery {
    const own = this.isCurrentUser(userId) ? this.own().delivery : null;
    return own ?? { userId, ...DEFAULT_NOTIFICATION_DELIVERY };
  }

  /**
   * A person's choices across a set of accounts, for the read-only administrator view. Tier limits
   * are applied so the summary says what is actually sent, not what was asked for.
   */
  summaryFor(
    userId: string,
    accounts: ReadonlyArray<{ readonly id: string; readonly name: string }>,
  ): AccountNotificationSummary[] {
    return accounts.map((account) => {
      const preference = this.accountPreference(userId, account.id);
      const allowsInstant = this.allowsInstantAlerts(account.id);
      return {
        accountId: account.id,
        accountName: account.name,
        reviewEmails: allowsInstant ? preference.reviewEmails : 'off',
        alertThreshold: preference.alertThreshold,
        summaries: allowsInstant
          ? preference.summaries
          : preference.summaries.filter((kind) => kind !== 'monthly'),
        custom: this.hasCustomAccountPreference(userId, account.id),
        allowsInstant,
      };
    });
  }

  /** Whether the account's tier allows instant review emails and the monthly report. */
  allowsInstantAlerts(accountId: string): boolean {
    const tierKey = this.tiers.subscriptionFor(accountId)?.tierKey ?? 'lite';
    return this.tiers.hasFeature(tierKey, INSTANT_ALERTS_FEATURE);
  }

  /**
   * Every per-person event, with the organisation's policy applied: what it offers, whether it is
   * switched off or mandatory, and what this person has chosen within that.
   */
  resolvedEvents(userId: string): ReadonlyArray<ResolvedEventPreference> {
    const policy = this.policies.forScope(this.organizations.activeId() ?? '');
    const rows = this.eventRows(userId);

    return this.catalog
      .filter((definition) => !ACCOUNT_LEVEL_EVENTS.has(definition.key))
      .map((definition) => {
        const rule = policy.rules.find((entry) => entry.event === definition.key);
        const available = PERSONAL_CHANNELS.filter(
          (channel) =>
            !!rule?.channels.includes(channel) &&
            !(channel === 'email' && EMAIL_HANDLED_PER_ACCOUNT.has(definition.key)),
        );
        const disabledByPolicy = !rule || !rule.isEnabled || available.length === 0;
        const mandatory = !disabledByPolicy && !!rule?.isMandatory;
        const chosen = rows.find((entry) => entry.event === definition.key)?.channels ?? available;

        return {
          event: definition.key,
          disabledByPolicy,
          mandatory,
          available,
          channels: disabledByPolicy
            ? []
            : mandatory
              ? available
              : chosen.filter((channel) => available.includes(channel)),
          recipientLabel: rule?.recipientLabel ?? '',
        };
      });
  }

  /** Whether the signed-in user should see this event in the notification centre. */
  receivesInApp(event: NotificationEventKey): boolean {
    const resolved = this.resolvedEvents(this.currentUser.id()).find((row) => row.event === event);
    return resolved ? resolved.channels.includes('in-app') : true;
  }

  saveAccountPreference(
    next: AccountNotificationPreference,
  ): Observable<AccountNotificationPreference> {
    const normalized = reconcileNotificationPreferences({ accounts: [next] }, this.currentUser.id())
      .accounts[0];
    if (!normalized) return throwError(() => new Error('An account is required'));
    const rows = this.own();
    return this.saveRows(
      {
        ...rows,
        accounts: [
          ...rows.accounts.filter((row) => row.accountId !== normalized.accountId),
          normalized,
        ],
      },
      normalized,
      'Updated their notification preferences',
    );
  }

  /** Applies one account's choices to every account the user can reach, as "apply to all". */
  applyToAccounts(
    source: AccountNotificationPreference,
    accountIds: ReadonlyArray<string>,
  ): Observable<void> {
    const rows = accountIds.map((accountId) => {
      const allowsInstant = this.allowsInstantAlerts(accountId);
      return {
        ...source,
        accountId,
        // Tier limits still apply to the copies: a LITE account cannot receive what it cannot send.
        reviewEmails: allowsInstant ? source.reviewEmails : 'off',
        summaries: allowsInstant
          ? source.summaries
          : source.summaries.filter((kind) => kind !== 'monthly'),
      } satisfies AccountNotificationPreference;
    });
    const stored = this.own();
    const normalized = reconcileNotificationPreferences(
      { accounts: rows },
      this.currentUser.id(),
    ).accounts;
    return this.saveRows(
      {
        ...stored,
        accounts: [
          ...stored.accounts.filter((row) => !accountIds.includes(row.accountId)),
          ...normalized,
        ],
      },
      undefined,
      'Applied notification preferences to all of their accounts',
    );
  }

  resetAccountPreference(accountId: string): Observable<void> {
    const rows = this.own();
    return this.saveRows(
      { ...rows, accounts: rows.accounts.filter((row) => row.accountId !== accountId) },
      undefined,
      'Reset their notification preferences for one account',
    );
  }

  saveEventChannels(
    event: NotificationEventKey,
    channels: ReadonlyArray<PersonalChannel>,
  ): Observable<void> {
    const row: EventNotificationPreference = { userId: this.currentUser.id(), event, channels };
    const rows = this.own();
    return this.saveRows(
      { ...rows, events: [...rows.events.filter((entry) => entry.event !== event), row] },
      undefined,
      'Updated their notification preferences',
    );
  }

  saveDelivery(next: UserNotificationDelivery): Observable<UserNotificationDelivery> {
    return this.saveRows(
      { ...this.own(), delivery: next },
      next,
      'Updated how their notifications are delivered',
    );
  }

  private saveRows<T>(
    rows: StoredNotificationPreferences,
    result: T,
    activity: string,
  ): Observable<T> {
    // A failed explicit save must leave the previously saved choices intact.
    try {
      const owner = this.owner();
      if (owner !== ANONYMOUS_PREFERENCES_OWNER) {
        const storage = this.document.defaultView?.localStorage;
        if (!storage) throw new Error('Preferences storage is unavailable');
        storage.setItem(storageKeyFor(owner), JSON.stringify(rows));
      }
    } catch (error) {
      return throwError(() => error);
    }
    this.own.set(rows);
    this.audit.recordOwn(activity);
    return of(result).pipe(delay(SAVE_LATENCY_MS));
  }

  private accountRows(userId: string): ReadonlyArray<AccountNotificationPreference> {
    if (this.isCurrentUser(userId)) return this.own().accounts;
    return ACCOUNT_NOTIFICATION_PREFERENCES_SEED.filter((row) => row.userId === userId);
  }

  private eventRows(userId: string): ReadonlyArray<EventNotificationPreference> {
    return this.isCurrentUser(userId) ? this.own().events : [];
  }

  private isCurrentUser(userId: string): boolean {
    return userId === this.currentUser.id();
  }

  private load(owner: string): StoredNotificationPreferences {
    const userId = this.currentUser.id();
    if (owner === ANONYMOUS_PREFERENCES_OWNER) {
      return reconcileNotificationPreferences(null, userId);
    }
    try {
      const raw = this.document.defaultView?.localStorage.getItem(storageKeyFor(owner));
      return reconcileNotificationPreferences(raw ? JSON.parse(raw) : null, userId);
    } catch {
      return reconcileNotificationPreferences(null, userId);
    }
  }
}

function storageKeyFor(owner: string): string {
  return `${NOTIFICATION_PREFERENCES_KEY_PREFIX}:${owner}`;
}
